"use client";

import React, { useEffect, useState, useMemo, useRef } from "react";
import Globe from "react-globe.gl";
import * as topojson from "topojson-client";
import { geoCentroid } from "d3-geo";

import travelData from "./travelData.json";
import { getFillColor, getToolTip, mapColors } from "./mapHelpers";

const DEFAULT_GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function FullScreenGlobe() {
  const [countries, setCountries] = useState([]);
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [size, setSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 800,
    height: typeof window !== "undefined" ? window.innerHeight : 600,
  });
  const globeRef = useRef(null);

  // Handle window resizing
  useEffect(() => {
    const onResize = () =>
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load world topojson
  useEffect(() => {
    async function loadWorld() {
      const res = await fetch(DEFAULT_GEO_URL);
      const worldTopoJson = await res.json();
      const countriesGeo = topojson.feature(
        worldTopoJson,
        worldTopoJson.objects.countries,
      );
      setCountries(countriesGeo.features || []);
    }
    loadWorld();
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls?.();
    if (!controls) return;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.6;
  }, [countries.length]);

  const getCountryName = (f) =>
    f?.properties?.name || f?.properties?.ADMIN || f?.properties?.NAME || "";

  const getCountryData = (f) =>
    travelData.find((c) => c.name === getCountryName(f));

  const hoveredTooltip = useMemo(() => {
    if (!hoveredCountry) return "";
    const name = getCountryName(hoveredCountry);
    const data = getCountryData(hoveredCountry);
    return getToolTip(name, data);
  }, [hoveredCountry]);

  // Labels
  const labelsData = useMemo(() => {
    return countries
      .map((feat) => {
        const name = getCountryName(feat);
        const [lng, lat] = geoCentroid(feat);
        if (!name || !isFinite(lng) || !isFinite(lat)) return null;
        return { name, lat, lng };
      })
      .filter(Boolean);
  }, [countries]);

  // Equator
  const equatorPath = useMemo(() => {
    const pts = [];
    for (let lng = -180; lng <= 180; lng += 2) {
      pts.push({ lat: 0, lng });
    }
    return pts;
  }, []);

  // get the number of countries visited both together, both separately
  const [bothTogether, bothSeparate, garrettOnly, kellyanOnly, none] =
    useMemo(() => {
      let bt = 0,
        bs = 0,
        go = 0,
        ko = 0,
        n = 0;
      travelData.forEach((country) => {
        if (country.visitedTogether) bt++;
        else if (country.kellyanVisited && country.garrettVisited) bs++;
        else if (country.garrettVisited) go++;
        else if (country.kellyanVisited) ko++;
        else n++;
      });
      return [bt, bs, go, ko, n];
    }, []);

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "black",
      }}
    >
      {countries.length > 0 && (
        <Globe
          ref={globeRef}
          width={size.width}
          height={size.height}
          backgroundColor="rgba(0,0,0,1)"
          polygonsData={countries}
          polygonCapColor={(feat) => getFillColor(getCountryData(feat))}
          polygonSideColor={() => "rgba(0,0,0,0.2)"}
          polygonStrokeColor={() => "rgba(255,255,255,0.4)"}
          polygonStrokeWidth={0.4}
          onPolygonHover={(feat) => setHoveredCountry(feat || null)}
          labelsData={labelsData}
          labelText={(d) => d.name}
          labelLat={(d) => d.lat}
          labelLng={(d) => d.lng}
          labelAltitude={() => 0.01}
          labelSize={() => 0.8}
          labelDotRadius={() => 0.15}
          labelColor={() => "white"}
          pathsData={[equatorPath]}
          pathPoints={(d) => d}
          pathPointLat={(p) => p.lat}
          pathPointLng={(p) => p.lng}
          pathColor={() => "rgba(220,220,220,0.8)"}
          pathStrokeWidth={() => 0.7}
        />
      )}

      {/* Tooltip */}
      {hoveredTooltip && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.6)",
            color: "white",
            padding: "6px 12px",
            borderRadius: "6px",
            fontSize: "14px",
            pointerEvents: "none",
          }}
        >
          {hoveredTooltip}
        </div>
      )}

      {/* ⭐ INFO KEY (Legend) ⭐ */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          background: "rgba(0,0,0,0.45)",
          padding: "12px 18px",
          borderRadius: "12px",
          backdropFilter: "blur(6px)",
          color: "white",
          fontSize: "13px",
          lineHeight: "20px",
          pointerEvents: "none", // ensures it doesn't block globe movement
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 4,
              background: mapColors.bothTogether,
            }}
          />
          Both Together ({bothTogether})
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 4,
              background: mapColors.bothSeparate,
            }}
          />
          Both Separate ({bothSeparate})
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 4,
              background: mapColors.garrettOnly,
            }}
          />
          Only Garrett ({garrettOnly})
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 4,
              background: mapColors.kellyanOnly,
            }}
          />
          Only Kellyan ({kellyanOnly})
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 4,
              background: mapColors.none,
            }}
          />
          Neither Visited ({none})
        </div>
      </div>
    </div>
  );
}
